<template>
  <button
    :type="type"
    class="text-button"
    :class="classes"
    :disabled="disabled"
  >
    <slot name="icon-start"></slot>

    <span v-if="$slots['icon-start-circle']" class="button__icon-start-circle">
      <slot name="icon-start-circle"></slot>
    </span>

    <slot></slot>

    <slot name="icon-end"></slot>

    <span v-if="$slots['icon-end-circle']" class="button__icon-end-circle">
      <slot name="icon-end-circle"></slot>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    variant?: 'contained' | 'outlined' | 'text'
    size?: 'small'
    color?: 'black'
    type?: 'button' | 'submit' | 'reset'
    fullWidth?: boolean
    disabled?: boolean
  }>(),
  {
    variant: 'contained',
    type: 'button',
  },
)

const classes = computed(() => ({
  'ui-button': true,
  [`ui-button--${props.variant}`]: true,
  [`ui-button--${props.size}`]: !!props.size,
  [`ui-button--${props.color}`]: !!props.color,
  'ui-button--full-width': props.fullWidth,
}))
</script>

<style lang="scss">
@import '../styles/config';

.ui-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  @include transition('color, background-color, border-color');
}

.ui-button--contained {
  color: $white;
  background-color: $red;
  border: 1px solid $red;

  &:hover,
  &:focus {
    background-color: $tint-red-hover;
    border-color: $tint-red-hover;
  }

  &.ui-button--black {
    background-color: $black;
    border-color: $black;

    &:hover,
    &:focus {
      background-color: $gray-400;
      border-color: $gray-400;
    }
  }
}

.ui-button--outlined {
  color: $black;
  border: 1px solid $black;

  &:hover,
  &:focus {
    color: $white;
    background-color: $black;
    border-color: $black;
  }
}

.ui-button--contained,
.ui-button--outlined {
  padding: 1rem 1.5rem;
  border-radius: 0.125rem;

  &:disabled,
  &[aria-disabled='true'] {
    color: $white;
    background-color: $gray-300;
    border-color: $gray-300;
    pointer-events: none;
  }

  &.ui-button--small {
    padding-top: 0.8125rem;
    padding-bottom: 0.8125rem;
  }

  @include media-breakpoint-up(md) {
    padding: 1.25rem 1.5rem;
  }
}

.ui-button--text {
  color: $black;
  font-size: 0.75rem;
  line-height: 1.66666667;
  letter-spacing: 0.08333333em;

  &:hover {
    color: $tint-red-hover;
  }
}

.ui-button--full-width {
  display: flex;
  width: 100%;
}

.button__icon-start-circle,
.button__icon-end-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid currentColor;
  border-radius: 50%;

  svg {
    width: 1rem;
    height: 1rem;
  }
}

.button__icon-start-circle {
  margin-right: 0.75rem;
}

.button__icon-end-circle {
  margin-left: 0.75rem;
}
</style>
